/**
 * Import Parser Service
 * 
 * Utilities for parsing and transforming imported files into Firestore-compatible formats.
 */

/**
 * Detect the type of file based on filename and content
 * @param {string} filename - The file name
 * @param {object|string} content - Parsed JSON or raw string content
 * @returns {'character' | 'shot' | 'markdown' | 'unknown'}
 */
export function detectFileType(filename, content) {
  // Markdown files
  if (filename.endsWith('.md')) {
    return 'markdown'
  }
  
  // JSON files - analyze content
  if (filename.endsWith('.json') && typeof content === 'object') {
    // Character sheet detection
    if (content.character_core || content.project_meta?.type === 'Character Reference Sheet') {
      return 'character'
    }
    
    // Frame/Shot detection
    if (content.shot_id || content.frame || content.cinematography) {
      return 'shot'
    }
  }
  
  return 'unknown'
}

/**
 * Parse a character JSON file into Firestore character format
 * @param {object} content - Parsed JSON content
 * @param {string} filename - Original filename for fallback data
 * @returns {object} Firestore-compatible character data
 */
export function parseCharacterJson(content, filename = '') {
  const core = content.character_core || {}
  const accessories = content.key_accessories || {}
  
  // Extract name - try multiple sources
  let name = core.name || ''
  if (!name && filename) {
    // Try to extract from filename like "arthur_sheet.json"
    const match = filename.match(/^([a-z]+)_sheet\.json$/i)
    if (match) {
      name = match[1].charAt(0).toUpperCase() + match[1].slice(1)
    }
  }
  
  return {
    name,
    description: core.basic_desc || '',
    hairStyle: core.hair_style || '',
    distinctFeatures: core.distinct_features || '',
    attire: core.clothing_outfit || '',
    accessories: accessories.items || '',
    primaryImage: '',
    extraImages: [],
    linkedLocations: [],
    linkedEvents: [],
    linkedShots: [],
    // Store original data for reference
    _importedFrom: filename,
    _importedAt: new Date().toISOString(),
  }
}

/**
 * Parse a frame JSON file into shot data
 * @param {object} content - Parsed JSON content
 * @param {string} filename - Original filename
 * @returns {object} Partial shot data (may need pairing with start/end)
 */
export function parseFrameJson(content, filename = '') {
  const isStart = content.frame === 'start' || filename.includes('_start')
  const isEnd = content.frame === 'end' || filename.includes('_end')
  
  // Extract layer from shot_id (e.g., "EP01_S01_B_EXT..." → "B")
  let layer = ''
  if (content.shot_id) {
    const layerMatch = content.shot_id.match(/_S\d+_([A-D])_/)
    if (layerMatch) {
      layer = layerMatch[1]
    }
  }
  
  // Create human-readable title from shot_id
  let title = ''
  if (content.shot_id) {
    // "EP01_S01_B_EXT_SKATE_HAL" → "S01 B EXT SKATE HAL"
    title = content.shot_id
      .replace(/^EP\d+_/, '')
      .replace(/_/g, ' ')
  }
  
  // Build description from subject and environment
  const descriptions = []
  if (content.subject?.description) {
    descriptions.push(content.subject.description)
  }
  if (content.environment?.location) {
    descriptions.push(`Location: ${content.environment.location}`)
  }
  if (content.action_moment?.dynamic_elements) {
    descriptions.push(`Action: ${content.action_moment.dynamic_elements}`)
  }
  
  return {
    shotId: content.shot_id || '',
    title,
    description: descriptions.join('\n\n'),
    durationSeconds: content.duration_seconds || 0,
    layer,
    aspectRatio: content.project_meta?.aspect_ratio || '4:3',
    frameType: isStart ? 'start' : isEnd ? 'end' : 'unknown',
    primaryImage: '',
    extraImages: [],
    linkedCharacters: [],
    linkedLocations: [],
    linkedEvents: [],
    // Store original data
    _importedFrom: filename,
    _importedAt: new Date().toISOString(),
    _rawData: content,
  }
}

/**
 * Pair start and end frame JSONs into a complete shot
 * @param {object} startFrame - Parsed start frame data
 * @param {object} endFrame - Parsed end frame data  
 * @returns {object} Complete shot data
 */
export function pairFramesToShot(startFrame, endFrame = null) {
  const shot = {
    shotId: startFrame.shotId || endFrame?.shotId || '',
    title: startFrame.title || endFrame?.title || '',
    description: startFrame.description || endFrame?.description || '',
    durationSeconds: startFrame.durationSeconds || endFrame?.durationSeconds || 0,
    layer: startFrame.layer || endFrame?.layer || '',
    aspectRatio: startFrame.aspectRatio || endFrame?.aspectRatio || '4:3',
    startFrame: startFrame._importedFrom || '',
    endFrame: endFrame?._importedFrom || '',
    primaryImage: '',
    extraImages: [],
    linkedCharacters: [],
    linkedLocations: [],
    linkedEvents: [],
  }
  
  return shot
}

/**
 * Parse a markdown file for synopsis/content
 * @param {string} content - Raw markdown string
 * @param {string} filename - Original filename
 * @returns {object} Content document data
 */
export function parseMarkdown(content, filename = '') {
  // Extract title from first H1
  let title = filename.replace('.md', '')
  const h1Match = content.match(/^#\s+(.+)$/m)
  if (h1Match) {
    title = h1Match[1]
  }
  
  return {
    markdown: content,
    title,
    _importedFrom: filename,
    _importedAt: new Date().toISOString(),
  }
}

/**
 * Group frame files by shot_id for pairing
 * @param {Array} frameFiles - Array of { filename, content, parsed } objects
 * @returns {Map} Map of shotId → { start, end } parsed frame data
 */
export function groupFramesByShot(frameFiles) {
  const groups = new Map()
  
  for (const file of frameFiles) {
    const shotId = file.parsed.shotId
    if (!shotId) continue
    
    if (!groups.has(shotId)) {
      groups.set(shotId, { start: null, end: null })
    }
    
    const group = groups.get(shotId)
    if (file.parsed.frameType === 'start') {
      group.start = file.parsed
    } else if (file.parsed.frameType === 'end') {
      group.end = file.parsed
    }
  }
  
  return groups
}

/**
 * Process all frame files into complete shots
 * @param {Array} frameFiles - Array of { filename, content, parsed } objects
 * @returns {Array} Array of complete shot objects ready for Firestore
 */
export function processFramesToShots(frameFiles) {
  const groups = groupFramesByShot(frameFiles)
  const shots = []
  
  for (const [shotId, { start, end }] of groups) {
    if (start) {
      shots.push(pairFramesToShot(start, end))
    } else if (end) {
      // Only have end frame, use it as primary
      shots.push(pairFramesToShot(end, null))
    }
  }
  
  return shots
}

/**
 * Create import preview summary
 * @param {Array} files - Array of parsed file objects
 * @returns {object} Summary of what will be imported
 */
export function createImportSummary(files) {
  const summary = {
    characters: [],
    shots: [],
    markdown: [],
    unknown: [],
    total: files.length,
  }
  
  for (const file of files) {
    switch (file.type) {
      case 'character':
        summary.characters.push(file)
        break
      case 'shot':
        summary.shots.push(file)
        break
      case 'markdown':
        summary.markdown.push(file)
        break
      default:
        summary.unknown.push(file)
    }
  }
  
  return summary
}

